import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, type ReactNode } from 'react';

import { loadStaff, type Staff } from '@/lib/auth';

const StaffContext = createContext<Staff | null>(null);

/** The signed-in staff member. Only under the shell, which renders nothing without one. */
export function useStaff(): Staff {
  const staff = useContext(StaffContext);
  if (!staff) throw new Error('useStaff outside the signed-in shell');
  return staff;
}

export function StaffProvider({
  staff,
  children,
}: {
  staff: Staff;
  children: ReactNode;
}) {
  return <StaffContext value={staff}>{children}</StaffContext>;
}

const SESSION_KEY = ['session'];

/** The session, read once per load and again when the window comes back into focus. */
export function useSession() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: loadStaff,
    staleTime: 60_000,
  });
}

/** Read the session again: what a sign-in asks for, so no form spells the key (`staff-sign-in-here` D9). */
export function useRefreshSession(): () => void {
  const client = useQueryClient();
  return useCallback(() => {
    void client.invalidateQueries({ queryKey: SESSION_KEY });
  }, [client]);
}
